import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEmpresa } from "@/context/EmpresaContext";
import api from "@/lib/api";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";

/**
 * Badge de "Notas presas" — mostra no topbar quantas NF-e/NFS-e estão autorizadas
 * mas ainda sem PDF publicado pela prefeitura/SEFAZ. Poll a cada 90s (o loop de
 * reconcile roda a cada 60s no backend, então 90s garante que o count sempre
 * espelha a última tentativa).
 */
export default function NotasPresasBadge() {
  const { empresaAtualId } = useEmpresa();
  const nav = useNavigate();
  const [data, setData] = useState({ count: 0, itens: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancel = false;
    const carregar = async () => {
      try {
        setLoading(true);
        const r = await api.get("/notas-fiscais/presas");
        if (!cancel) setData(r.data || { count: 0, itens: [] });
      } catch {
        if (!cancel) setData({ count: 0, itens: [] });
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    carregar();
    const t = setInterval(carregar, 90000);
    return () => { cancel = true; clearInterval(t); };
  }, [empresaAtualId]);

  // Não renderiza quando não tem nada preso — evita ruído visual
  if (!data.count && !loading) return null;

  const irParaNfse = (tipo) => {
    nav(tipo === "NFE" ? "/nfe" : "/nfse");
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="notas-presas-badge"
          className="relative inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 transition"
          title="Notas autorizadas aguardando a prefeitura publicar o PDF"
        >
          {loading
            ? <Loader2 className="w-4 h-4 animate-spin text-amber-700" />
            : <AlertTriangle className="w-4 h-4 text-amber-700" />}
          <span className="text-xs font-semibold font-mono-fin"
                data-testid="notas-presas-count">
            {data.count}
          </span>
          <span className="hidden md:inline text-[11px] text-amber-800">
            {data.count === 1 ? "nota aguardando prefeitura" : "notas aguardando prefeitura"}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 border-amber-200"
                       data-testid="notas-presas-popover">
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5">
          <div className="text-sm font-semibold text-amber-900">
            {data.count} nota{data.count === 1 ? "" : "s"} aguardando prefeitura
          </div>
          <div className="text-[11px] text-amber-800 mt-0.5">
            Autorizada{data.count === 1 ? "" : "s"} pela SEFAZ mas o PDF ainda não
            foi publicado. O sistema retenta até 5x automaticamente (cada 60s).
          </div>
        </div>
        <ul className="max-h-80 overflow-auto divide-y divide-slate-100">
          {(data.itens || []).slice(0, 15).map((n) => (
            <li key={n.id}
                className="px-4 py-2 hover:bg-slate-50 cursor-pointer transition"
                onClick={() => irParaNfse(n.tipo)}
                data-testid={`notas-presas-item-${n.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">
                    {n.tipo === "NFE" ? "NF-e" : "NFS-e"} #{n.numero_provider || n.numero}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {n.cliente_nome || "Cliente"}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] font-mono text-amber-700">
                    tent. {n.pdf_retry_count || 0}/5
                  </div>
                </div>
              </div>
            </li>
          ))}
          {data.itens.length > 15 && (
            <li className="px-4 py-2 text-[11px] text-slate-500 text-center">
              +{data.itens.length - 15} mais…
            </li>
          )}
        </ul>
        <div className="border-t border-slate-200 px-4 py-2 bg-slate-50 text-[10px] text-slate-500">
          Clique numa nota para ir ao painel e acompanhar
        </div>
      </PopoverContent>
    </Popover>
  );
}
